import { CalendarSync, Pencil } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { StatusLine } from "./StatusLine";
import { SWAP } from "./swap";
import { BigNumber, Tile, TileAction, type TileEdit } from "./Tile";
import { useTilePulse } from "./useTilePulse";
import { useShortTokenFormat, useTokenFormat } from "./useTokenFormat";
import { useSetMonthlyCap } from "../../api/useCaptainBillingMutations";
import { getCaptainBalanceState } from "../../balance-state";
import type { CaptainBillingAccount } from "../../types";
import { capResetDate, capRunOut, capUsedPercent } from "../../utils";
import { CAP_FILL } from "../balance-tab-variants";
import { InlineTokensEditor, type TokensInput, type TokensSetError } from "../editors/InlineTokensEditor";
import { capSchema } from "../editors/tokensSchema";
import { MeterRow } from "../MeterRow";

import { Progress } from "@/components/ui/v2/progress";
import { formatDayMonth } from "@/utils/dateUtils";

export function CapTile({ account, edit }: { account: CaptainBillingAccount; edit?: TileEdit }) {
  const { t, i18n } = useTranslation();
  const formatTokens = useTokenFormat();
  const shortTokens = useShortTokenFormat();
  const { save, isPending } = useSetMonthlyCap();
  const state = getCaptainBalanceState(account);
  const usedPercent = Math.round(capUsedPercent(account));
  const hasCap = account.monthlyCapTokens > 0;
  const capLeft = Math.max(0, account.capLeftTokens);
  const pulse = useTilePulse(capLeft);
  const runOut = capRunOut(account, new Date());

  const saveCap = (tokens: number, setError: TokensSetError) =>
    save<TokensInput>({ clientId: account.clientId, monthlyCapTokens: tokens }, setError, () => edit?.onDone());

  return (
    <Tile
      icon={CalendarSync}
      label={t("captainBilling.tiles.cap")}
      hint={t("captainBilling.tiles.hint.cap")}
      pulse={pulse}
      corner={
        edit &&
        !edit.isEditing && <TileAction label={t("captainBilling.actions.editCap")} icon={Pencil} onClick={edit.onEdit} />
      }
    >
      <AnimatePresence mode="wait" initial={false}>
        {edit?.isEditing ? (
          <motion.div key="edit" {...SWAP}>
            <InlineTokensEditor
              schema={capSchema(t)}
              label={t("captainBilling.editors.cap.label")}
              defaultValue={account.monthlyCapTokens}
              isPending={isPending}
              onSave={saveCap}
              onCancel={edit.onDone}
              help={(tokens) =>
                t(tokens > 0 ? "captainBilling.editors.cap.help" : "captainBilling.editors.cap.helpZero", {
                  tokens: formatTokens(tokens),
                })
              }
            />
          </motion.div>
        ) : (
          <motion.div key="view" {...SWAP} className="space-y-2.5">
            <BigNumber
              value={capLeft}
              qualifier={hasCap ? t("captainBilling.tiles.leftOf", { total: shortTokens(account.monthlyCapTokens) }) : undefined}
            />
            <MeterRow aside={hasCap ? t("captainBilling.tiles.usedPercent", { percent: usedPercent }) : undefined}>
              <Progress
                label={t("captainBilling.tiles.capUsedLabel", { percent: usedPercent })}
                value={usedPercent}
                segments={[{ percent: usedPercent, className: CAP_FILL[state] }]}
              />
            </MeterRow>
            <p className="text-sm text-muted-foreground tabular-nums">
              {hasCap
                ? t("captainBilling.tiles.capFootnote", {
                  resetDate: formatDayMonth(capResetDate(account.month), i18n.language, "short"),
                })
                : t("captainBilling.tiles.noCap")}
            </p>
            {hasCap && capLeft <= 0 && (
              <StatusLine tone={account.topUpBalanceTokens > 0 ? "warning" : "error"}>
                {t(`captainBilling.tiles.capUsedUp.${account.topUpBalanceTokens > 0 ? "onTopUp" : "paused"}`)}
              </StatusLine>
            )}
            {runOut instanceof Date && (
              <StatusLine tone="warning">
                {t("captainBilling.tiles.pace.runsOut", { date: formatDayMonth(runOut, i18n.language, "short") })}
              </StatusLine>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Tile>
  );
}
